/**
 * Turn 2: visibly harder/easier because of turn 1; Pam voices adaptationReason.
 */
import React, { useMemo, useRef, useState } from 'react';
import type { AdaptiveQuestionResult } from '../types';
import {
  buildAdaptiveResult,
  pamAdaptationCopy,
  pickFollowupQuestion,
  type AdaptationReason,
  type AdaptiveTurnQuestion,
} from '../adaptiveEngine';
import { AdaptiveQuestionBody } from './DiagnosticQuestion';
import { PamNote } from './PamNote';

export interface AdaptiveFollowupTurnProps {
  turn1Result: Pick<AdaptiveQuestionResult, 'questionId' | 'correct' | 'difficultyShown'>;
  question?: AdaptiveTurnQuestion;
  adaptationReason?: AdaptationReason;
  commitTurn?: (result: AdaptiveQuestionResult) => void | Promise<void>;
  onComplete: (result: AdaptiveQuestionResult) => void;
}

export const AdaptiveFollowupTurn: React.FC<AdaptiveFollowupTurnProps> = ({
  turn1Result,
  question: questionProp,
  adaptationReason: reasonProp,
  commitTurn,
  onComplete,
}) => {
  const picked = useMemo(() => pickFollowupQuestion(turn1Result), [turn1Result]);
  const question = questionProp ?? picked.question;
  const adaptationReason = reasonProp ?? picked.adaptationReason;
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const done = useRef(false);

  const finish = async () => {
    if (done.current || choiceId === null) return;
    done.current = true;
    const result = buildAdaptiveResult({
      turnId: 2,
      kind: 'adaptive_followup',
      question,
      choiceId,
      adaptationReason,
    });
    if (commitTurn) await commitTurn(result);
    onComplete(result);
  };

  return (
    <section data-testid="adaptive-followup-turn" data-turn="2" className="space-y-5">
      <div data-testid="adaptation-reason" data-reason={adaptationReason}>
        <PamNote label="Pam's read">{pamAdaptationCopy(adaptationReason)}</PamNote>
      </div>
      <AdaptiveQuestionBody
        question={question}
        choiceId={choiceId}
        onSubmit={setChoiceId}
        onContinue={() => void finish()}
      />
    </section>
  );
};
